import React, { useEffect, useState } from 'react';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { listCastingSignups, updateCastingSignup } from '../../../services/SubmissionService';
import { listCities } from '../../../services/UserService';
import './Casting.css';

const getStatusClass = (status) => String(status || 'unknown').toLowerCase().replace(/\s+/g, '-');
const requestValue = (signup, field) => signup[field] ?? signup.data?.[field];
const getSignupId = (signup) => requestValue(signup, 'uuid') || requestValue(signup, 'id');
const loadErrorMessage = (error) => error instanceof TypeError
    ? 'Unable to connect to the casting applications service.'
    : error.message || 'Unable to load casting applications.';
const displayValue = (value) => value === null || value === undefined || value === '' ? '—' : String(value);
const phoneDigits = (value) => String(value || '').replace(/\D/g, '');
const phoneHref = (value) => String(value || '').replace(/[^\d+]/g, '');
const renderEmail = (value) => value
    ? <a className="email-link" href={`mailto:${value}`} title={value}>{value}</a>
    : '—';
const renderPhone = (value) => value
    ? <a href={`tel:${phoneHref(value)}`}>{value}</a>
    : '—';
const renderWhatsapp = (value) => {
    if (!value) return '—';
    const digits = phoneDigits(value);
    return digits ? <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer">WhatsApp</a> : displayValue(value);
};
const renderTelegram = (value) => {
    if (!value) return '—';
    const username = String(value).replace(/^@/, '');
    return username ? <a href={`https://t.me/${username}`} target="_blank" rel="noreferrer">{value}</a> : displayValue(value);
};
const getPhotoUrl = (photo) => typeof photo === 'string' ? photo : photo?.url || photo?.photoUrl || photo?.src || '';
const formatDetailLabel = (field) => field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, character => character.toUpperCase());
const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) return value.length ? value.map(item => typeof item === 'object' ? JSON.stringify(item) : item).join(', ') : '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};
const detailSummaryFields = new Set(['data', 'uuid', 'id', 'name', 'email', 'phone', 'whatsapp', 'telegram', 'age', 'city', 'cityUuid', 'cityName', 'nationality', 'experience', 'about', 'status', 'photos']);
const getSignupDetails = (signup) => Object.entries({ ...signup, ...(signup.data || {}) })
    .filter(([field]) => !detailSummaryFields.has(field));
const statuses = ['Pending', 'Reviewed', 'Approved', 'Rejected'];

const Casting = () => {
    const [signups, setSignups] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedSignup, setSelectedSignup] = useState(null);
    const [statusValue, setStatusValue] = useState('Pending');
    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [notice, setNotice] = useState('');

    const loadSignups = async () => {
        setError('');
        setIsLoading(true);
        try {
            const result = await listCastingSignups();
            setSignups(result.items);
        } catch (loadError) {
            setError(loadErrorMessage(loadError));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadSignups(); }, []);

    useEffect(() => {
        let isMounted = true;
        listCities()
            .then(items => {
                if (isMounted) setCities(Array.isArray(items) ? items : []);
            })
            .catch(() => {
                if (isMounted) setCities([]);
            });
        return () => { isMounted = false; };
    }, []);

    const cityNames = Object.fromEntries(cities.map(city => [city.uuid, city.name || city.title || city.slug]));
    const getCityName = (signup) => {
        const city = requestValue(signup, 'city');
        const cityUuid = requestValue(signup, 'cityUuid');
        if (city && typeof city === 'object') return city.name || city.title || city.slug || '—';
        return cityNames[cityUuid] || city || requestValue(signup, 'cityName') || '—';
    };

    const openSignup = (signup) => {
        setSelectedSignup(signup);
        setStatusValue(requestValue(signup, 'status') || 'Pending');
        setStatusError('');
        setNotice('');
    };

    const saveStatus = async () => {
        if (!selectedSignup || !getSignupId(selectedSignup) || !statusValue) return;
        setIsSavingStatus(true);
        setStatusError('');
        try {
            const updatedSignup = await updateCastingSignup(getSignupId(selectedSignup), { status: statusValue });
            setSignups(current => current.map(item => getSignupId(item) === getSignupId(selectedSignup) ? { ...item, ...updatedSignup, status: updatedSignup?.status || statusValue } : item));
            setSelectedSignup(current => ({ ...current, ...updatedSignup, status: updatedSignup?.status || statusValue }));
            setNotice(`Application status updated to ${updatedSignup?.status || statusValue}.`);
        } catch (saveError) {
            setStatusError(loadErrorMessage(saveError));
        } finally {
            setIsSavingStatus(false);
        }
    };

    return (
        <div className="casting">
            <NavigationMenu />
            <div className="casting-content">
                <header className="casting-header"><h1 className="casting-title">Casting</h1><p className="casting-subtitle">Review new girls who want to join the agency</p></header>
                <main className="casting-main">
                    {notice && <div className="casting-notice" role="status">{notice}</div>}
                    {isLoading ? <div className="submission-feedback">Loading casting applications...</div> : error ? (
                        <div className="submission-feedback submission-error" role="alert"><p>{error}</p><button type="button" className="feedback-button" onClick={loadSignups}>Try again</button></div>
                    ) : signups.length === 0 ? (
                        <div className="submission-empty"><h2>No casting applications</h2><p>New applications will appear here when they are submitted.</p></div>
                    ) : (
                        <div className="table-container"><div className="table-header"><div><h2 className="table-title">Applications</h2><p className="table-caption">{signups.length} application{signups.length === 1 ? '' : 's'}</p></div></div><div className="table-scroll"><table className="data-table"><thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>WhatsApp</th><th>City</th><th>Age</th><th>Experience</th><th>Status</th><th>Actions</th></tr></thead><tbody>
                            {signups.map(signup => <tr key={getSignupId(signup)}><td>{displayValue(requestValue(signup, 'name'))}</td><td>{renderEmail(requestValue(signup, 'email'))}</td><td>{renderPhone(requestValue(signup, 'phone'))}</td><td>{renderWhatsapp(requestValue(signup, 'whatsapp'))}</td><td>{getCityName(signup)}</td><td>{displayValue(requestValue(signup, 'age'))}</td><td>{displayValue(requestValue(signup, 'experience')) === '—' || String(requestValue(signup, 'experience')).toLowerCase() === 'none' ? displayValue(requestValue(signup, 'experience')) : <>Years: {displayValue(requestValue(signup, 'experience'))} Years</>}</td><td><span className={`status-badge ${getStatusClass(requestValue(signup, 'status'))}`}>{displayValue(requestValue(signup, 'status'))}</span></td><td><button type="button" className="action-btn view" onClick={() => openSignup(signup)}>Review</button></td></tr>)}
                        </tbody></table></div></div>
                    )}
                </main>
            </div>

            {selectedSignup && <div className="casting-modal-overlay" role="presentation"><div className="casting-modal" role="dialog" aria-modal="true" aria-labelledby="casting-detail-title"><div className="modal-header"><div><span className="modal-kicker">Application {getSignupId(selectedSignup)}</span><h3 className="modal-title" id="casting-detail-title">{displayValue(requestValue(selectedSignup, 'name'))}</h3></div><button type="button" className="modal-close" onClick={() => setSelectedSignup(null)} aria-label="Close casting application">×</button></div><div className="modal-body"><div className="detail-grid"><div><span>Email</span><strong>{renderEmail(requestValue(selectedSignup, 'email'))}</strong></div><div><span>Phone</span><strong>{renderPhone(requestValue(selectedSignup, 'phone'))}</strong></div><div><span>WhatsApp</span><strong>{renderWhatsapp(requestValue(selectedSignup, 'whatsapp'))}</strong></div><div><span>Telegram</span><strong>{renderTelegram(requestValue(selectedSignup, 'telegram'))}</strong></div><div><span>Age</span><strong>{displayValue(requestValue(selectedSignup, 'age'))}</strong></div><div><span>City</span><strong>{getCityName(selectedSignup)}</strong></div><div><span>Nationality</span><strong>{displayValue(requestValue(selectedSignup, 'nationality'))}</strong></div><div><span>Experience</span><strong>{displayValue(requestValue(selectedSignup, 'experience'))}</strong></div></div><div className="detail-block"><span>About</span><p>{displayValue(requestValue(selectedSignup, 'about'))}</p></div>{getSignupDetails(selectedSignup).length > 0 && <div className="detail-block"><span>All application data</span><div className="detail-grid detail-fields">{getSignupDetails(selectedSignup).map(([field, value]) => <div key={field}><span>{formatDetailLabel(field)}</span><strong>{formatDetailValue(value)}</strong></div>)}</div></div>}{Array.isArray(requestValue(selectedSignup, 'photos')) && requestValue(selectedSignup, 'photos').length > 0 && <div className="detail-block"><span>Photos</span><div className="photo-grid">{requestValue(selectedSignup, 'photos').map((photo, index) => { const photoUrl = getPhotoUrl(photo); return photoUrl ? <a className="casting-photo-link" href={photoUrl} target="_blank" rel="noreferrer" key={`${photoUrl}-${index}`}><img src={photoUrl} alt={`Applicant submission ${index + 1}`} loading="lazy" /><span>Photo {index + 1}</span></a> : null; })}</div></div>}<div className="status-editor"><label htmlFor="casting-status">Application status</label><select id="casting-status" className="modal-input" value={statusValue} onChange={event => setStatusValue(event.target.value)}>{statuses.map(status => <option key={status} value={status}>{status}</option>)}</select>{statusError && <p className="field-error" role="alert">{statusError}</p>}</div></div><div className="modal-footer"><button type="button" className="modal-button cancel" onClick={() => setSelectedSignup(null)}>Close</button><button type="button" className="modal-button create" onClick={saveStatus} disabled={isSavingStatus}>{isSavingStatus ? 'Saving...' : 'Save Status'}</button></div></div></div>}
        </div>
    );
};

export default Casting;